import React, { useState, useRef, useEffect } from 'react';

interface ImageCompareSliderProps {
  beforeImageSrc: string;
  afterImageSrc: string;
  beforeLabel?: string;
  afterLabel?: string;
  objectFit?: 'object-cover' | 'object-contain';
  objectPosition?: string;
  beforeImageScale?: number; // New prop for scaling the before image
  afterImageScale?: number;  // New prop for scaling the after image
}

const ImageCompareSlider: React.FC<ImageCompareSliderProps> = ({
  beforeImageSrc,
  afterImageSrc,
  beforeLabel = 'COMPETITORS',
  afterLabel = 'CR3ATE.AI',
  objectFit = 'object-cover',
  objectPosition,
  beforeImageScale = 1,
  afterImageScale = 1
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const updateSlider = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const newX = clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (newX / rect.width) * 100));
    setSliderPosition(percent);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) updateSlider(e.clientX);
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging && e.touches.length > 0) {
        updateSlider(e.touches[0].clientX);
      }
    };
    const handleMouseUp = () => setIsDragging(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  // Create style objects for image scaling
  const beforeImageStyle = {
    transform: `scale(${beforeImageScale})`,
    transformOrigin: 'center center'
  };

  const afterImageStyle = {
    transform: `scale(${afterImageScale})`,
    transformOrigin: 'center center'
  };

  return (
    <div
      ref={containerRef}
      className="relative mx-auto overflow-hidden rounded-xl border border-[#0CB4E4]/30 shadow-xl select-none"
      style={{ width: '100%', maxWidth: '1200px', aspectRatio: '16/9' }}
    >
      {/* After Image */}
      <img
        src={afterImageSrc}
        alt="After"
        className={`absolute inset-0 w-full h-full ${objectFit}`}
        style={{
          ...afterImageStyle,
          objectPosition: objectPosition || 'center'
        }}
        draggable={false}
      />
      {sliderPosition < 90 && (
        <div className="absolute top-4 right-4 px-3 py-1 bg-[#161513] text-white font-semibold text-sm rounded-full shadow-lg z-20">
          {afterLabel}
        </div>
      )}

      {/* Before Image with clip-path */}
      <div
        className="absolute inset-0 z-10"
        style={{
          clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
        }}
      >
        <img
          src={beforeImageSrc}
          alt="Before"
          className={`absolute inset-0 w-full h-full ${objectFit}`}
          style={{
            ...beforeImageStyle,
            objectPosition: objectPosition || 'center'
          }}
          draggable={false}
        />
        <div className="absolute top-4 left-4 px-3 py-1 bg-[#161513] text-white font-semibold text-sm rounded-full shadow-lg">
          {beforeLabel}
        </div>
      </div>

      {/* Slider Line + Handle */} 
      <div
        className="absolute top-0 bottom-0 w-1 z-30"
        style={{
          left: `${sliderPosition}%`,
          transform: 'translateX(-50%)',
          cursor: isDragging ? 'grabbing' : 'grab',
          backgroundColor: '#161513',
          boxShadow: '0 0 6px rgba(0,0,0,0.5)',
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 border-2 border-[#161513] shadow-sm flex items-center justify-center backdrop-blur-sm">
          <div className="w-2.5 h-2.5 bg-white rounded-full" />
        </div>
      </div>
    </div>
  );
};

export default ImageCompareSlider;